/* eslint-disable react/jsx-indent */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { BsFilter } from 'react-icons/bs';
import { MdRemoveRedEye } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';

import { Radio, CircularProgress } from '@material-ui/core';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import moment from 'moment';

import DateRange from '~/components/DateRange';
import FilterChips from '~/components/FilterChips';
import Select from '~/components/Form/Select';
import InputSearchDebounce, {
  SearchInputImperativeProps,
} from '~/components/InputSearchDebounce';
import Sidesheet from '~/components/Sidesheet';
import PaginatedTable from '~/components/Table/PaginatedTable';
import { PaginatedTableProps } from '~/components/Table/PaginatedTable/interface';
import { useAuth } from '~/hooks/auth';
import { useNavigation } from '~/hooks/Navigation';
import { IState } from '~/store';
import { getProcessesRequest } from '~/store/modules/processes/actions';
import {
  getCompletedTasksRequest,
  getDeletedTasksRequest,
  getTaskRequest,
} from '~/store/modules/tasks/actions';
import { ITaskHistory } from '~/store/modules/tasks/types';
import { permissions } from '~/types/constants';

import TaskDetails from '../TaskDetails';
import {
  Container,
  FormControl,
  FormControlLabel,
  RadioGroup,
  FilterButton,
  Wrapper,
  FilterWrapper,
  SidesheetCustomStyle,
  FilterTitle,
  SubmitFilterButton,
} from './styles';

moment.locale('pt-br');

interface FormTableData {
  processDefinitionNames?: string[];
  assigneeIn?: string[];
  endTime?: string[];
}

type FilterParams = 'assigneeIn' | 'processDefinitionNames' | 'endTime';

const HistoryTasks: React.FC = () => {
  const [resetPage, setResetPage] = useState<boolean>(false);
  const [customLimit, setCustomLimit] = useState<number>(15);

  const [searchTaskHistory, setSearchTaskHistory] = useState<string>('');
  const [modalTaskHistoryFilter, setModalTaskHistoryFilter] = useState<boolean>(
    false,
  );

  const [usersActiveFiltered, setUserActiveFiltered] = useState<any>([]);

  const [completedTasksFilter, setCompletedTasksFilter] = useState<boolean>(
    true,
  );
  const [deletedTasksFilter, setDeletedTasksFilter] = useState<boolean>(false);

  const [modalTaskDetail, setModalTaskDetail] = useState<boolean>(false);
  const [, setSelectedTask] = useState<ITaskHistory | null>(null);
  const loading = useSelector((state: IState) => state.tasks.loading);

  const [filterParams, setFilterParams] = useState<FormTableData>({});

  const sortOrder = useSelector((state: IState) => state.tasks.sortOrder);

  const completedTasks = useSelector(
    (state: IState) => state.tasks.completedTasks,
  );
  const totalCompletedTasks = useSelector(
    (state: IState) => state.tasks.totalCompletedTasks,
  );

  const deletedTasks = useSelector((state: IState) => state.tasks.deletedTasks);
  const totalDeletedTasks = useSelector(
    (state: IState) => state.tasks.totalDeletedTasks,
  );

  const task = useSelector((state: IState) => state.tasks.task);

  const activeUsers = useSelector(
    (state: IState) => state.users.activatedUsers,
  );

  const usersByGroups = useSelector(
    (state: IState) => state.users.usersByGroups,
  );

  const processes = useSelector((state: IState) => state.processes.processes);

  const formTableRef = useRef<FormHandles>(null);
  const searchInputRef = useRef<SearchInputImperativeProps>(null);

  const dispatch = useDispatch();
  const { checkPermission, user } = useAuth();
  const { confirmAndNavigate, setCanNavigate } = useNavigation();

  const loadTasks = useCallback(
    (params, changeOrder) => {
      if (
        user.groups.length === 1 &&
        checkPermission([permissions.VIEW_CLIENT_REQUEST])
      ) {
        Object.assign(params, {
          ...params,
          candidateGroups: ['client'],
        });
      }
      if (completedTasksFilter) {
        Object.assign(params, {
          ...params,
          taskDeleteReason: 'completed',
          orgId: user.orgId,
        });
        dispatch(getCompletedTasksRequest(params, changeOrder));
      } else {
        Object.assign(params, {
          ...params,
          taskDeleteReason: 'deleted',
          orgId: user.orgId,
        });
        dispatch(getDeletedTasksRequest(params, changeOrder));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [completedTasksFilter, dispatch, user.orgId],
  );

  const handleChangeRadio = useCallback(() => {
    confirmAndNavigate(() => {
      if (searchInputRef?.current) searchInputRef.current.handleReset();
      setCompletedTasksFilter(!completedTasksFilter);
      setDeletedTasksFilter(!deletedTasksFilter);
      setFilterParams({});
      setResetPage(true);
      setCustomLimit(15);

      const changeOrder = false;

      const params = {
        page: 1,
        limit: 15,
        sortBy: 'endTime',
      };

      loadTasks(params, changeOrder);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAndNavigate, completedTasksFilter, deletedTasksFilter]);

  const handleFilterParams = () => {
    const data = formTableRef.current?.getData() as FormTableData;

    const newFilterParams: FormTableData = {};

    if (
      data?.processDefinitionNames &&
      data.processDefinitionNames?.length > 0
    ) {
      Object.assign(newFilterParams, {
        processDefinitionNames: data.processDefinitionNames,
      });
    }

    if (data?.assigneeIn && data.assigneeIn?.length > 0) {
      Object.assign(newFilterParams, {
        assigneeIn: data.assigneeIn.map((assignee) => assignee),
      });
    }

    if (data?.endTime && data.endTime[0] && data.endTime[1]) {
      Object.assign(newFilterParams, {
        endTime: data.endTime,
      });
    }

    setFilterParams(newFilterParams);
  };

  const handleFilterTableTasks = useCallback(
    (page: number, limit: number) => {
      const searchTasksHistory = searchTaskHistory;

      const changeOrder = false;

      const params = {
        page: page || 1,
        limit,
        sortBy: 'endTime',
        processDefinitionNames:
          filterParams.processDefinitionNames?.map((process) => process) ?? [],
        assigneeIn:
          filterParams.assigneeIn?.map((assignee: any) => assignee.id) ?? [],
        endTime: filterParams.endTime,
        taskSearch: searchTasksHistory,
      };

      loadTasks(params, changeOrder);
    },
    [loadTasks, filterParams, searchTaskHistory],
  );

  const handleDeleteChip = useCallback(
    (key: FilterParams, selectedChip?: string) => {
      if (
        (key === 'assigneeIn' || key === 'processDefinitionNames') &&
        selectedChip
      ) {
        const filteredKey = filterParams[key]?.filter(
          (selected) => selected !== selectedChip,
        );

        const newFilterParams = {
          ...filterParams,
          [key]: filteredKey,
        };

        if (filteredKey && filteredKey?.length < 1) delete newFilterParams[key];
        setFilterParams(newFilterParams);
      } else if (key === 'endTime') {
        const newFilterParams = filterParams;
        delete newFilterParams[key];

        setResetPage(true);

        setFilterParams({ ...newFilterParams });
      }
    },
    [filterParams],
  );

  const handleChangeOrder = useCallback(() => {
    const changeOrder = true;
    const params = {
      page: 1,
      limit: customLimit,
      sortBy: 'endTime',
      processDefinitionNames:
        filterParams.processDefinitionNames?.map((process) => process) ?? [],
      assigneeIn:
        filterParams.assigneeIn?.map((assignee: any) => assignee.id) ?? [],
      endTime: filterParams.endTime,
      taskSearch: searchTaskHistory,
    };

    setResetPage(true);
    loadTasks(params, changeOrder);
  }, [
    customLimit,
    filterParams.assigneeIn,
    filterParams.endTime,
    filterParams.processDefinitionNames,
    loadTasks,
    searchTaskHistory,
  ]);

  const handleWhichTaskToRender = useCallback(() => {
    if (completedTasksFilter) {
      return completedTasks;
    }

    return deletedTasks;
  }, [completedTasksFilter, completedTasks, deletedTasks]);

  const handleChangePage = useCallback(
    (page, limit) => {
      setCustomLimit(limit);

      handleFilterTableTasks(page, limit);
    },
    [handleFilterTableTasks],
  );

  const handleModalHistoryTaskDetail = useCallback(
    (task: ITaskHistory | null) => {
      if (task) dispatch(getTaskRequest(task.id));

      setModalTaskDetail(!modalTaskDetail);
      setSelectedTask(task);
    },
    [dispatch, modalTaskDetail],
  );

  useEffect(() => {
    const changeOrder = false;
    const params = {
      page: 1,
      limit: 15,
      sortBy: 'endTime',
      taskDeleteReason: 'deleted',
      candidateGroups: ['client'],
    };

    dispatch(getDeletedTasksRequest(params, changeOrder));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const page = 1;

    const canNavigate =
      !searchTaskHistory &&
      (!filterParams || Object.entries(filterParams).length < 1);

    setCanNavigate(canNavigate);

    handleFilterTableTasks(page, customLimit);
  }, [
    customLimit,
    dispatch,
    filterParams,
    handleFilterTableTasks,
    searchTaskHistory,
    setCanNavigate,
  ]);

  useEffect(() => {
    dispatch(getProcessesRequest());
  }, [dispatch]);

  useEffect(() => {
    const filteredUsers: any = [];

    if (
      user.groups.length === 1 &&
      checkPermission([permissions.VIEW_CLIENT_REQUEST])
    ) {
      usersByGroups.forEach((userByGroup) => {
        const teste = activeUsers.filter(
          (activeUser) => String(activeUser.id) === String(userByGroup.id),
        );

        filteredUsers.push(...teste);
      });

      setUserActiveFiltered(filteredUsers);
    } else {
      setUserActiveFiltered(activeUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableProps: PaginatedTableProps<ITaskHistory> = {
    entities: handleWhichTaskToRender() && handleWhichTaskToRender(),
    loading,
    columns: [
      {
        header: 'Processo',
        value: (task) => task.processDefinitionName,
      },
      {
        id: 'due',
        header: 'Conclusão',
        value: (task) => (
          <>{moment(new Date(task.endTime)).format('DD/MM/YYYY HH:mm')}</>
        ),
      },
      {
        header: 'Solicitação',
        value: (task) => task.nomeSolicitacao,
      },
      {
        header: 'Execução',
        value: (task) => task.nomeExecucao,
      },
      {
        header: 'Tarefa',
        value: (task) => task.name,
      },
      {
        header: 'Responsável',
        value: (task) => task.assigneeName,
      },
    ],
    sorting: {
      sortBy: 'due',
      sortOrder,
      onChange: () => handleChangeOrder(),
    },
    emptyTable: {
      title: 'Nenhuma tarefa finalizada',
      description:
        'O sistema ainda não possui nenhuma tarefa finalizada.\nContate o administrador.',
    },
    menu: [
      {
        label: 'Visualizar detalhes',
        Icon: MdRemoveRedEye,
        action: (task) => handleModalHistoryTaskDetail(task),
        permissions: [permissions.VIEW_DETAILS_TASK],
      },
    ],
    paginationProps: {
      total: completedTasksFilter ? totalCompletedTasks : totalDeletedTasks,
      onChange: (page: number, limit: number) => handleChangePage(page, limit),
      resetPage,
      onReset: (reseted) => setResetPage(reseted),
    },
  };

  return (
    <Container>
      <Wrapper>
        <FormControl>
          <RadioGroup row>
            <FormControlLabel
              value={completedTasksFilter}
              control={<Radio />}
              label={`Concluídas (${totalCompletedTasks})`}
              checked={completedTasksFilter}
              onChange={() => handleChangeRadio()}
            />
            <FormControlLabel
              value={deletedTasksFilter}
              control={<Radio />}
              label={`Canceladas (${totalDeletedTasks})`}
              checked={deletedTasksFilter}
              onChange={() => handleChangeRadio()}
            />
          </RadioGroup>
        </FormControl>

        <FilterWrapper>
          <InputSearchDebounce
            ref={searchInputRef}
            placeholder="Buscar uma solicitação ou execução"
            getSearch={(search) => setSearchTaskHistory(search)}
          />
          <FilterButton
            type="button"
            onClick={() => setModalTaskHistoryFilter(!modalTaskHistoryFilter)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                <BsFilter size={20} color="white" />
                Filtrar
              </>
            )}
          </FilterButton>
        </FilterWrapper>
      </Wrapper>

      <FilterChips
        filterParams={[
          {
            list: filterParams.assigneeIn,
            label: (assignee) => `${assignee.firstName} ${assignee.lastName}`,
            onDelete: (assignee) =>
              handleDeleteChip('assigneeIn', String(assignee.id)),
          },
          {
            list: filterParams.processDefinitionNames,
            label: (process) => process,
            onDelete: (process) =>
              handleDeleteChip('processDefinitionNames', process),
          },
          {
            condition: !!filterParams.endTime,
            label: `${moment(filterParams.endTime?.[0]).format(
              'DD/MM/YYYY',
            )} - ${moment(filterParams.endTime?.[1]).format('DD/MM/YYYY')}`,
            onDelete: () => handleDeleteChip('endTime'),
          },
        ]}
        onClear={() => setFilterParams({})}
      />

      <Sidesheet
        title="Filtro"
        open={modalTaskHistoryFilter}
        modalState={() => setModalTaskHistoryFilter(false)}
        sidesheetSize="450px"
      >
        <Form
          ref={formTableRef}
          onSubmit={() => {}}
          style={{
            margin: '16px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: '16px',
          }}
        >
          <div>
            <FilterTitle>Processo</FilterTitle>
            <Select
              name="processDefinitionNames"
              placeholder=""
              isClearable
              closeMenuOnSelect={false}
              defaultValue={
                filterParams.processDefinitionNames &&
                filterParams.processDefinitionNames.length > 0
                  ? filterParams.processDefinitionNames.map((process) => ({
                      label: process,
                      value: process,
                    }))
                  : null
              }
              options={processes?.map((process) => {
                return {
                  value: process.name,
                  label: process.name,
                };
              })}
              isMulti
            />
            <FilterTitle>Período de conclusão</FilterTitle>
            <DateRange
              name="endTime"
              hideCalendar
              defaultStartDate={
                filterParams.endTime
                  ? moment(filterParams.endTime[0], 'DD/MM/YYYY').toDate()
                  : null
              }
              defaultEndDate={
                filterParams.endTime
                  ? moment(filterParams.endTime[1], 'DD/MM/YYYY').toDate()
                  : null
              }
            />

            <FilterTitle>Responsável</FilterTitle>
            <Select
              name="assigneeIn"
              placeholder=""
              isClearable
              closeMenuOnSelect={false}
              defaultValue={
                filterParams.assigneeIn && filterParams.assigneeIn.length > 0
                  ? filterParams.assigneeIn.map((assignee: any) => ({
                      label: `${assignee.firstName} ${assignee.lastName}`,
                      value: assignee,
                    }))
                  : null
              }
              options={
                usersActiveFiltered.length >= 1
                  ? usersActiveFiltered.map((activeUser: any) => {
                      return {
                        value: activeUser,
                        label: `${activeUser.firstName} ${activeUser.lastName}`,
                      };
                    })
                  : []
              }
              isMulti
            />
          </div>
          <SubmitFilterButton
            type="button"
            onClick={() => {
              handleFilterParams();
              setModalTaskHistoryFilter(false);
              setResetPage(true);
            }}
          >
            Filtrar
          </SubmitFilterButton>
        </Form>

        <SidesheetCustomStyle />
      </Sidesheet>

      <PaginatedTable {...tableProps} />

      {/* TODO(Cabral): Passar taskInstanceId no lugar de processInstance */}
      <TaskDetails
        open={modalTaskDetail}
        modalState={() => handleModalHistoryTaskDetail(null)}
        taskInstance={task}
      />
    </Container>
  );
};

export default HistoryTasks;
